'use client';

/**
 * PlanCard — the shared tier-card shell used by the public /pricing page
 * (PricingV2Section for user plans, ProfessionalPricingSection for pro plans).
 *
 * The card is a slotted layout, not a god-component:
 *   - ``priceDisplay``  → PlanPriceDisplay (caller pre-computes VAT/annual)
 *   - ``features``      → PlanFeatureList
 *   - ``footer``        → free-form ReactNode (usually a Button)
 *
 * Business concerns stay in the caller:
 *   - Auth/signup-modal handling  (PricingV2Section)
 *   - Router → /checkout base64   (PricingV2Section)
 *   - Auth + checkout redirects  (all callers, per-surface)
 *   - VAT math, annual math       (both — via pricing-helpers)
 *
 * Visual concerns live here:
 *   - Card border, padding, optional 2px brand-tertiary popular glow
 *   - "Most popular" badge (absolutely positioned)
 *   - Name heading + optional description + optional trial info
 *   - HR separator between price and features
 *   - Feature list via PlanFeatureList
 *   - Footer slot anchored to bottom via ``mt-auto``
 */

import { Check, Sparkles } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

import { type CheckmarkVariant, PlanFeatureList } from './PlanFeatureList';
import {
  PlanPriceDisplay,
  type PlanPriceDisplayProps,
} from './PlanPriceDisplay';

// Optional fields use ``?: T | undefined`` explicitly so callers can build
// the props object with conditional values without tripping the project's
// ``exactOptionalPropertyTypes: true`` tsconfig.
interface PlanCardProps {
  /** Plan title — already localized. Rendered as ``<h3>``. */
  name: string;
  /**
   * Short sub-title under the name. Already localized. Omit to skip the
   * paragraph entirely.
   */
  description?: string | undefined;
  /**
   * Trial-specific nudge ("14 days free, no card"). Rendered in brand
   * colour between description and the HR divider.
   */
  trialInfo?: string | undefined;

  /**
   * Personalised recommendation badge derived from the user's trial usage.
   * Rendered as a subtle strip directly above the card title ("Odporúčané pre vás").
   * Only shown when a recommendation is available for the current user. Omit to skip.
   */
  recommendationBadge?: string | undefined;
  /**
   * Optional explanatory tooltip for the recommendation badge (e.g. "Based on
   * your usage during the trial"). Surfaced as a native ``title`` so the
   * "personalised" claim is qualified. Omit to render the badge without a tooltip.
   */
  recommendationBadgeTooltip?: string | undefined;

  /**
   * Prominent value-add strip rendered directly under the price (e.g. "Profil+
   * listing included free" on the Lawyers tab). Brand-tinted with a check so it
   * reads as bundled value, not a feature bullet. Omit to skip.
   */
  bundleBadge?: string | undefined;

  /**
   * Muted footnote rendered below the feature list, above the footer (e.g. "Or
   * get Profil+ free with a Starter or Pro plan" cross-reference). Omit to skip.
   */
  footnote?: string | undefined;

  /** PlanPriceDisplay props — the whole price block. */
  priceDisplay: PlanPriceDisplayProps;

  /** Feature strings (already localized). Rendered via PlanFeatureList. */
  features: string[];
  /** Check-mark colour variant — default ``brand``. */
  checkVariant?: CheckmarkVariant | undefined;

  /** When true, renders the popular badge + 2px brand-tertiary border. */
  isPopular?: boolean | undefined;
  /** e.g. "Most popular" / "Najpopulárnejšie". Shown when ``isPopular``. */
  popularLabel?: string | undefined;

  /**
   * When true, dims the card and applies the muted border. Use for plans
   * below the user's current tier (upgrade dialog) or plans the user can
   * no longer select.
   */
  isDimmed?: boolean | undefined;

  /** CTA content — usually a ``<Button>``. Pinned to card bottom. */
  footer: ReactNode;

  className?: string | undefined;
}

export function PlanCard({
  name,
  description,
  trialInfo,
  recommendationBadge,
  recommendationBadgeTooltip,
  bundleBadge,
  footnote,
  priceDisplay,
  features,
  checkVariant = 'brand',
  isPopular = false,
  popularLabel,
  isDimmed = false,
  footer,
  className,
}: PlanCardProps) {
  return (
    <div
      className={cn(
        'relative bg-surface-card rounded-xl border flex flex-col h-full p-8',
        isPopular
          ? 'border-2 border-brand-tertiary shadow-[0_0_30px_-8px_var(--brand-tertiary)]'
          : 'border-line-default',
        isDimmed && 'opacity-50 border-line-subtle',
        className
      )}
    >
      {isPopular && popularLabel && (
        <div className='absolute -top-3.5 left-1/2 -translate-x-1/2'>
          <span className='bg-brand-tertiary text-content-inverse text-xs font-semibold px-4 py-1 rounded-full whitespace-nowrap'>
            {popularLabel}
          </span>
        </div>
      )}

      {/* Personalised recommendation badge — shown only when trial usage data
          is available. Distinct from isPopular (marketing-static) — this is
          user-specific and derived from actual trial usage (#5554). */}
      {recommendationBadge && (
        <div
          className='mb-2 inline-flex items-center gap-1 self-start rounded-full bg-status-success/15 px-2.5 py-0.5 text-xs font-medium text-status-success'
          title={recommendationBadgeTooltip}
        >
          <Sparkles className='h-3 w-3 flex-shrink-0' aria-hidden='true' />
          {recommendationBadge}
        </div>
      )}

      <h3 className='text-lg font-semibold text-content-primary'>{name}</h3>

      <PlanPriceDisplay {...priceDisplay} />

      {bundleBadge && (
        <div className='mt-3 flex items-center gap-1.5 rounded-lg bg-brand-tertiary/10 px-3 py-2 text-sm font-semibold text-brand-tertiary-color'>
          <Check className='h-4 w-4 flex-shrink-0' aria-hidden='true' />
          <span>{bundleBadge}</span>
        </div>
      )}

      {description && (
        <p className='mt-3 text-sm text-content-secondary'>{description}</p>
      )}

      {trialInfo && (
        <p className='mt-2 text-sm font-medium text-brand-tertiary'>
          {trialInfo}
        </p>
      )}

      <hr className='my-6 border-line-default' />

      <PlanFeatureList
        features={features}
        ariaLabel={`${name} features`}
        checkVariant={checkVariant}
      />

      {footnote && (
        <p className='mt-4 text-xs text-content-muted'>{footnote}</p>
      )}

      {/* Footer sticks to the bottom of a variable-height card row. */}
      <div className='mt-auto pt-8'>{footer}</div>
    </div>
  );
}
